import React from 'react';
import { Box, Divider, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import Ameneties from './Ameneties';
import LaunchRoundedIcon from '@mui/icons-material/LaunchRounded';
import { COLOR_PRIMARY_2 } from 'src/utils/colors';
import { formatCurrency } from 'src/utils/functions';
import airbnb from 'src/App/assets/airbnbMini.svg';
import bookingCom from 'src/App/assets/bookingComMini.jpeg';
import vrbo from 'src/App/assets/vrboMini.png';

const ImageGrid = ({ imageLinks, listingUrl, isMobileScreen, listingData }) => {
  const isOnlyOneListingPresent = imageLinks.length === 1;

  return (
    <Box
      onClick={() => window.open(listingUrl)}
      sx={{
        position: 'relative',
        display: 'flex',
        flexWrap: 'nowrap',
        height: '200px',
        width: '100%',
        gap: isOnlyOneListingPresent ? 0 : '10px',
        cursor: 'pointer',
      }}
    >
      <Box
        sx={{
          flex: 1,
          height: '100%',
          borderRadius: '10px',
          overflow: 'hidden',
        }}
      >
        <img
          src={imageLinks[0]}
          alt="Main"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      </Box>

      {/* Smaller Images */}
      {!isOnlyOneListingPresent && !isMobileScreen && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
            flex: 1,
            gap: '20px',
          }}
        >
          {imageLinks.slice(1).map((src, index) => (
            <Box
              key={index}
              sx={{
                width: 'calc(50% - 20px)',
                height: 'calc(50% - 10px)',
                borderRadius: '10px',
                overflow: 'hidden',
              }}
            >
              <img
                src={src}
                alt={`Thumbnail ${index + 1}`}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            </Box>
          ))}
        </Box>
      )}

      <Tooltip title="View on Airbnb">
        <IconButton
          onClick={() => window.open(listingUrl)}
          sx={{
            position: 'absolute',
            top: '30px',
            left: '30px',
            background: '#fff',
            border: '1px solid #ccc',
            boxShadow: '0 8px 24px -4px #0004',
            ':hover': {
              background: '#fff',
            },
          }}
        >
          <img
            src={airbnb}
            style={{
              width: '25px',
              height: '25px',
              objectFit: 'contain',
            }}
          />
        </IconButton>
      </Tooltip>

      {listingData?.['bookingComURL'] && (
        <Tooltip title="View on Booking.com">
          <IconButton
            onClick={() => {
              window.open(
                `https://www.booking.com/hotel/${listingData?.['bookingComURL']}`,
                '_blank',
                'noopener,noreferrer'
              );
            }}
            sx={{
              position: 'absolute',
              top: '30px',
              left: '80px',
              background: '#fff',
              border: '1px solid #ccc',
              boxShadow: '0 8px 24px -4px #0004',
              ':hover': {
                background: '#fff',
              },
            }}
          >
            <img
              src={bookingCom}
              style={{
                width: '25px',
                height: '25px',
              }}
            />
          </IconButton>
        </Tooltip>
      )}

      {listingData?.['vrboURL'] && (
        <Tooltip title="View on VRBO">
          <IconButton
            onClick={() =>
              window.open(`https://www.vrbo.com/${listingData?.['vrboURL']}`, '_blank')
            }
            sx={{
              position: 'absolute',
              top: '30px',
              left: listingData?.['bookingComURL'] ? '130px' : '80px',
              background: '#fff',
              border: '1px solid #ccc',
              boxShadow: '0 8px 24px -4px #0004',
              ':hover': {
                background: '#fff',
              },
            }}
          >
            <img
              src={vrbo}
              style={{
                width: '25px',
                height: '25px',
              }}
            />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

const TitleBar = ({ data }) => {
  return (
    <>
      <Grid
        container
        flexDirection={'row'}
        justifyContent={'space-between'}
        alignItems={'center'}
        sx={{ width: '100%' }}
      >
        <Typography variant="h6" sx={{ marginBottom: '20px' }}>
          Entire home hosted by <span style={{ color: COLOR_PRIMARY_2 }}>{data['host_name']}</span>
        </Typography>

        <Grid
          item
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '10px',
            paddingBottom: '20px',
          }}
        >
          <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <span style={{ fontWeight: 'bold' }}>
              <span style={{ color: 'gold' }}>★</span> {data['review_scores_rating']}
            </span>{' '}
            <Typography variant="body1">Rating</Typography>
          </Typography>
          <Divider orientation="vertical" variant="middle" flexItem />
          <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <span style={{ fontWeight: 'bold' }}>
              {data['visible_review_count']
                ? formatCurrency(String(data['visible_review_count']))
                : data['visible_review_count']}
            </span>{' '}
            <Typography variant="body1">Reviews</Typography>
          </Typography>
        </Grid>
      </Grid>

      <Grid
        container
        flexDirection={'row'}
        justifyContent={'space-between'}
        alignItems={'center'}
        sx={{ width: '100%', padding: '0 0 20px 20px' }}
      >
        {[
          { title: 'Bedrooms', value: data['bedrooms'] },
          { title: 'Bathrooms', value: data['bathrooms'] },
          { title: 'Accommodates', value: data['accommodates'] },
        ].map(data => (
          <Typography variant="h5">
            <span style={{ fontWeight: 'bold', color: COLOR_PRIMARY_2 }}>{data['value']}</span>
            <Typography variant="body1">{data['title']}</Typography>
          </Typography>
        ))}
      </Grid>
    </>
  );
};

const ListingDetailsSection = ({
  data,
  isMobileScreen,
  cachedMarketData,
  PerformanceExplanationComponent,
}) => {
  const thumbnailUrl = data['thumbnail_url'];
  const listingUrl = `https://www.airbnb.com/rooms/${data['listingID']}`;

  let restThumbnails = data['rest_thumbnails'];
  if (restThumbnails && typeof restThumbnails === 'string') {
    restThumbnails = restThumbnails.replace('[', '').replace(']', '');
    restThumbnails = Array.from(restThumbnails.replace(/'/gi, '').split(','));
    restThumbnails = restThumbnails.map(
      thumbnail => `https://a0.muscache.com/im/pictures/${thumbnail.trim()}?aki_policy=small`
    );
  }

  const imageLinks = [thumbnailUrl, ...(restThumbnails ? restThumbnails : [])]
    .slice(0, 5)
    .map(image => image.split('?')[0]);

  const marketAmenityDistro = cachedMarketData ? cachedMarketData.supply_breakdown?.amenity : null;

  return (
    <>
      <Grid container direction="row" justifyContent="center" xs={12}>
        <Grid
          container
          item
          direction="row"
          justifyContent="space-between"
          xs={12}
          alignItems="flex-start"
          sx={{ padding: '20px' }}
        >
          <Grid item xs={12} md={5} sx={{ paddingTop: isMobileScreen ? '20px' : 0 }}>
            <TitleBar data={data} />
          </Grid>
          <Grid item xs={12} md={6.5}>
            <ImageGrid
              imageLinks={imageLinks}
              listingUrl={listingUrl}
              isMobileScreen={isMobileScreen}
              listingData={data}
            />
          </Grid>
        </Grid>

        <Grid
          container
          flexDirection={'row'}
          justifyContent={'flex-start'}
          alignItems={'center'}
          sx={{ width: '100%' }}
        >
          {PerformanceExplanationComponent}
        </Grid>

        <Grid
          container
          flexDirection={'row'}
          justifyContent={'space-between'}
          alignItems={'center'}
          sx={{ width: '100%', padding: '0 0 20px 20px' }}
        >
          <Ameneties
            data={data}
            isMobileScreen={isMobileScreen}
            marketAmenityDistro={marketAmenityDistro}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default ListingDetailsSection;
